import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import PaletteIcon from "@mui/icons-material/Palette";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  updateSupabasePreferences,
  selectPreferences,
} from "store/PreferenceSlice";
import {
  getBackgroundColorsList,
  getBackgroundColor,
  isValidColorName,
  isHexColor,
} from "services/backgroundColors";
import { BACKGROUND_IMAGES } from "constants/backgrounds";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`background-tabpanel-${index}`}
      aria-labelledby={`background-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function BackgroundSelectionModal({ open, onClose }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const preferences = useSelector(selectPreferences);
  const [tabValue, setTabValue] = useState(0);
  const [selectedType, setSelectedType] = useState("image"); // 'image' or 'color'
  const [selectedValue, setSelectedValue] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Initialize selected values from preferences
  useEffect(() => {
    if (preferences) {
      const bgType = preferences.backgroundType || "image";
      let bgValue = preferences.backgroundValue || BACKGROUND_IMAGES[0]?.key;

      // Handle backward compatibility: if it's a hex color, try to map it to a color name
      if (bgType === "color" && isHexColor(bgValue)) {
        // Try to find a matching color name for the hex value
        const colorsList = getBackgroundColorsList();
        const matchingColor = colorsList.find(
          (color) => color.light === bgValue || color.dark === bgValue
        );
        if (matchingColor) {
          bgValue = matchingColor.key;
        }
        // If no match found, keep the hex value (will be handled as legacy)
      }

      setSelectedType(bgType);
      setSelectedValue(bgValue);
      // Set tab based on type
      setTabValue(bgType === "image" ? 0 : 1);
    }
  }, [preferences, open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedType(newValue === 0 ? "image" : "color");
  };

  const handleImageSelect = (imageKey) => {
    setSelectedValue(imageKey);
    setSelectedType("image");
  };

  const handleColorSelect = (colorKey) => {
    setSelectedValue(colorKey);
    setSelectedType("color");
  };

  const handleSave = async () => {
    if (!selectedValue) {
      setError("Please select a background image or color");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await dispatch(
        updateSupabasePreferences({
          backgroundType: selectedType,
          backgroundValue: selectedValue,
        })
      ).unwrap();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save background preference");
    } finally {
      setProcessing(false);
    }
  };

  const currentSelection =
    selectedType === "image"
      ? BACKGROUND_IMAGES.find((img) => img.key === selectedValue)
      : getBackgroundColorsList().find((color) => color.key === selectedValue);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
        }}
      >
        <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
          Customize Background
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<ImageIcon />}
            iconPosition="start"
            label="Background Images"
          />
          <Tab
            icon={<PaletteIcon />}
            iconPosition="start"
            label="Background Colors"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, overflowY: "auto", maxHeight: "60vh" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a background image
          </Typography>
          <Grid container spacing={2}>
            {BACKGROUND_IMAGES.map((image) => {
              const isSelected =
                selectedType === "image" && selectedValue === image.key;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={image.key}>
                  <Paper
                    onClick={() => handleImageSelect(image.key)}
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      overflow: "hidden",
                      border: isSelected
                        ? `3px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 200,
                        backgroundImage: `url('${image.thumbnail || image.path}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "background.paper",
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        color={isSelected ? "primary.main" : "text.primary"}
                      >
                        {image.name}
                      </Typography>
                    </Box>
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderRadius: "50%",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a background color (automatically adapts to light/dark mode)
          </Typography>
          <Grid container spacing={2}>
            {getBackgroundColorsList().map((color) => {
              const isSelected =
                selectedType === "color" && selectedValue === color.key;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={color.key}>
                  <Paper
                    onClick={() => handleColorSelect(color.key)}
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      border: isSelected
                        ? `3px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    {/* Show both light and dark mode previews */}
                    <Box
                      sx={{
                        width: "100%",
                        height: 120,
                        display: "flex",
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        overflow: "hidden",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          bgcolor: color.light,
                          position: "relative",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            left: 4,
                            fontSize: "0.65rem",
                            color: "text.secondary",
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            px: 0.5,
                            borderRadius: 0.5,
                          }}
                        >
                          Light
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          bgcolor: color.dark,
                          position: "relative",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            fontSize: "0.65rem",
                            color: "text.secondary",
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            px: 0.5,
                            borderRadius: 0.5,
                          }}
                        >
                          Dark
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "background.paper",
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        color={isSelected ? "primary.main" : "text.primary"}
                      >
                        {color.name}
                      </Typography>
                    </Box>
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderRadius: "50%",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        {/* {currentSelection && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected: {currentSelection.name}
            </Typography>
          </Box>
        )} */}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={processing || !selectedValue}
          startIcon={processing ? <CircularProgress size={16} /> : null}
        >
          {processing ? "Saving..." : "Save Background"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
